/**
 * LOCAL EMULATOR PORTS — one place the web app learns where the emulators listen.
 *
 * Normal dev uses the firebase.json defaults (auth 9099, functions 5001, firestore 8080, storage 9199) and sets
 * none of these variables, so it is unchanged. The isolated e2e stack (`npm run e2e:stack` + `npm run e2e:web`,
 * firebase.e2e.json) runs a SECOND emulator suite on offset ports beside the owner's dev stack, and points the
 * app at it through REACT_APP_EMULATOR_*_PORT.
 *
 * Pure (no Firebase import) so `utils/apiClient` can use it without pulling the SDK into every test.
 */
export interface EmulatorPorts {
  auth: number;
  functions: number;
  firestore: number;
  storage: number;
}

export const DEFAULT_EMULATOR_PORTS: EmulatorPorts = { auth: 9099, functions: 5001, firestore: 8080, storage: 9199 };

function portFrom(raw: string | undefined, fallback: number): number {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 && n < 65536 ? n : fallback;
}

/** Reads the ports from an env map; any missing or malformed value keeps its default. */
export function readEmulatorPorts(env: Record<string, string | undefined>): EmulatorPorts {
  return {
    auth: portFrom(env.REACT_APP_EMULATOR_AUTH_PORT, DEFAULT_EMULATOR_PORTS.auth),
    functions: portFrom(env.REACT_APP_EMULATOR_FUNCTIONS_PORT, DEFAULT_EMULATOR_PORTS.functions),
    firestore: portFrom(env.REACT_APP_EMULATOR_FIRESTORE_PORT, DEFAULT_EMULATOR_PORTS.firestore),
    storage: portFrom(env.REACT_APP_EMULATOR_STORAGE_PORT, DEFAULT_EMULATOR_PORTS.storage),
  };
}

// CRA inlines `process.env.REACT_APP_*` only when referenced by its literal name, so the map is spelled out.
export const emulatorPorts: EmulatorPorts = readEmulatorPorts({
  REACT_APP_EMULATOR_AUTH_PORT: process.env.REACT_APP_EMULATOR_AUTH_PORT,
  REACT_APP_EMULATOR_FUNCTIONS_PORT: process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT,
  REACT_APP_EMULATOR_FIRESTORE_PORT: process.env.REACT_APP_EMULATOR_FIRESTORE_PORT,
  REACT_APP_EMULATOR_STORAGE_PORT: process.env.REACT_APP_EMULATOR_STORAGE_PORT,
});
